import { supabase } from '../lib/supabase';

export interface TransactionToImport {
  date: string;
  label: string;
  amount: number;
  category_id?: string | null;
  notes?: string;
  treasury_account_id?: string | null;
}

export interface DeduplicationResult {
  newTransactions: TransactionToImport[];
  duplicates: TransactionToImport[];
  totalCount: number;
  newCount: number;
  duplicateCount: number;
}

function normalizeLabel(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s]/g, '');
}

function createDeduplicationKey(
  date: string,
  amount: number,
  label: string,
  accountId: string | null | undefined
): string {
  const normalizedLabel = normalizeLabel(label);
  const accountKey = accountId || 'unassigned';
  return `${date}|${amount}|${normalizedLabel}|${accountKey}`;
}

export async function deduplicateTransactions(
  transactionsToImport: TransactionToImport[],
  userId: string
): Promise<DeduplicationResult> {
  if (transactionsToImport.length === 0) {
    return {
      newTransactions: [],
      duplicates: [],
      totalCount: 0,
      newCount: 0,
      duplicateCount: 0
    };
  }

  const startDate = transactionsToImport.reduce((min, t) =>
    t.date < min ? t.date : min, transactionsToImport[0].date
  );

  const endDate = transactionsToImport.reduce((max, t) =>
    t.date > max ? t.date : max, transactionsToImport[0].date
  );

  const { data: existingTransactions, error } = await supabase
    .from('transactions')
    .select('date, label, amount, treasury_account_id')
    .eq('user_id', userId)
    .gte('date', startDate)
    .lte('date', endDate);

  if (error) {
    console.error('Error fetching existing transactions:', error);
    return {
      newTransactions: transactionsToImport,
      duplicates: [],
      totalCount: transactionsToImport.length,
      newCount: transactionsToImport.length,
      duplicateCount: 0
    };
  }

  const existingKeys = new Set(
    existingTransactions.map(t =>
      createDeduplicationKey(t.date, parseFloat(t.amount.toString()), t.label, t.treasury_account_id)
    )
  );

  const newTransactions: TransactionToImport[] = [];
  const duplicates: TransactionToImport[] = [];

  for (const transaction of transactionsToImport) {
    const key = createDeduplicationKey(
      transaction.date,
      transaction.amount,
      transaction.label,
      transaction.treasury_account_id
    );

    if (existingKeys.has(key)) {
      duplicates.push(transaction);
    } else {
      newTransactions.push(transaction);
      existingKeys.add(key);
    }
  }

  return {
    newTransactions,
    duplicates,
    totalCount: transactionsToImport.length,
    newCount: newTransactions.length,
    duplicateCount: duplicates.length
  };
}

export async function insertTransactionsWithDeduplication(
  transactions: TransactionToImport[],
  userId: string,
  importedFrom: string = ''
): Promise<{ success: boolean; insertedCount: number; duplicateCount: number; error?: string }> {
  try {
    const deduplicationResult = await deduplicateTransactions(transactions, userId);

    if (deduplicationResult.newCount === 0) {
      return {
        success: true,
        insertedCount: 0,
        duplicateCount: deduplicationResult.duplicateCount
      };
    }

    const transactionsToInsert = deduplicationResult.newTransactions.map(t => ({
      user_id: userId,
      date: t.date,
      label: t.label,
      amount: t.amount,
      category_id: t.category_id || null,
      notes: t.notes || '',
      imported_from: importedFrom,
      treasury_account_id: t.treasury_account_id || null,
      is_internal_transfer: false
    }));

    const { error } = await supabase
      .from('transactions')
      .insert(transactionsToInsert);

    if (error) throw error;

    return {
      success: true,
      insertedCount: deduplicationResult.newCount,
      duplicateCount: deduplicationResult.duplicateCount
    };
  } catch (error: any) {
    console.error('Error inserting transactions:', error);
    return {
      success: false,
      insertedCount: 0,
      duplicateCount: 0,
      error: error.message
    };
  }
}
